import type { Call } from '@entities/call';
import type { BaseUsecase } from '@usecases/base-usecase';
import { CallStatuses, CallTypes } from '@entities/call';

export type BlindTransferNumberRequest = {
  callId: string;
  to: string;
  name?: string;
};

export interface BlindTransferNumberRepository {
  getCurrentCall(): Promise<Call>;
  insertOutboundCall(call: Call): Promise<void>;
  updateHoldStatus(id: string, isOnHold: boolean): Promise<void>;
}

export interface BlindTransferNumberUserRepository {
  getOutboundNumber(): Promise<string>;
}

export interface BlindTransferNumberEventPublisher {
  publish(call: Call): Promise<void>;
}

export interface BlindTransferNumberUsecase
  extends BaseUsecase<BlindTransferNumberRequest, void> {
  run(call: BlindTransferNumberRequest): Promise<void>;
}

export class BlindTransferNumber implements BlindTransferNumberUsecase {
  constructor(
    private userRepository: BlindTransferNumberUserRepository,
    private repository: BlindTransferNumberRepository,
    private publisher: BlindTransferNumberEventPublisher,
  ) {}

  async run({
    callId: id,
    to,
    name,
  }: BlindTransferNumberRequest): Promise<void> {
    const from = await this.userRepository.getOutboundNumber();
    const currentCall = await this.repository.getCurrentCall();
    const call: Call = {
      id,
      to,
      from,
      type: CallTypes.ADD_PARTICIPANT,
      status: CallStatuses.DIALING,
      isMuted: false,
      isOnHold: false,
      name,
    };

    await Promise.all([
      this.publisher.publish(call),
      this.repository.insertOutboundCall(call),
      currentCall && this.repository.updateHoldStatus(currentCall.id, true),
    ]);
  }
}
